/**
 * L1 API 错误展示：i18n、去重、批处理聚合
 * 供 request 拦截器与 runApiBatch 共用
 */

import { message } from 'ant-design-vue'
import i18n from '@/i18n'
import {
  L1Error,
  NETWORK_ERROR,
  isL1Error,
  toL1Error,
} from './l1-error'

/** L1 标准码 → i18n 键 */
const L1_CODE_I18N: Record<number, string> = {
  4010: 'error.l1.unauthorized',
  4030: 'error.l1.forbidden',
  4040: 'error.l1.notFound',
  4220: 'error.l1.validation',
  5000: 'error.l1.internal',
  [NETWORK_ERROR]: 'error.l1.network',
}

/** 5xxx 业务码细粒度映射（与 backend ErrorCodes Q00x → 5600+ 对齐） */
const L1_BUSINESS_CODE_I18N: Record<number, string> = {
  5601: 'error.quotaExceeded',
  5602: 'error.quotaInsufficient',
  5603: 'error.quotaCheckFailed',
  5604: 'error.quotaConsumeFailed',
}

/** 4010 不弹 toast 的认证相关路由（与 router/guards 对齐） */
export const AUTH_ROUTE_WHITELIST_PATHS = ['/login', '/register'] as const

export function isAuthRoutePath(pathname: string): boolean {
  return AUTH_ROUTE_WHITELIST_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  )
}

/** 主错误优先级（数值越小越优先） */
const PRIMARY_CODE_PRIORITY: Record<number, number> = {
  4010: 0,
  4030: 1,
  4040: 2,
  4220: 3,
  5000: 4,
}

const DEDUPE_MS = 300
/** 批处理聚合窗口（并行请求落桶） */
const BATCH_FLUSH_MS = 0

export interface ShowApiErrorOptions {
  /** 显式 i18n 键，优先于码表 */
  errorI18nKey?: string
}

export interface MaybePresentErrorConfig extends ShowApiErrorOptions {
  skipErrorToast?: boolean
  errorBatchId?: string
  /** 触发错误的请求 URL（用于 401 白名单细分） */
  requestUrl?: string
  /** 触发错误的 HTTP 方法 */
  requestMethod?: string
}

interface BatchBucket {
  errors: L1Error[]
  options?: ShowApiErrorOptions
  timer: ReturnType<typeof setTimeout> | null
}

let lastDedupeKey = ''
let lastDedupeAt = 0
const batchBuckets = new Map<string, BatchBucket>()

/**
 * 从多个 L1 错误中选取主错误（4010 > 4030 > 4040 > 4220 > 5000 > 其他）
 */
export function pickPrimaryError(errors: L1Error[]): L1Error | undefined {
  if (errors.length === 0) {
    return undefined
  }
  return errors.reduce((primary, current) => {
    const primaryRank = PRIMARY_CODE_PRIORITY[primary.code] ?? 999
    const currentRank = PRIMARY_CODE_PRIORITY[current.code] ?? 999
    if (currentRank < primaryRank) {
      return current
    }
    if (currentRank > primaryRank) {
      return primary
    }
    return current.code <= primary.code ? current : primary
  })
}

/** 优先展示后端业务文案（message / fields）的标准码 */
const BACKEND_DETAIL_CODES = new Set<number>([4010, 4030, 4040, 4220, 5000])

/** L1 4220 单字段校验项 */
export interface ValidationFieldError {
  field: string
  message: string
}

/** L1 4220 data.fields：dict 或 { field, message }[] → 结构化列表 */
export function extractValidationFieldErrors(err: L1Error): ValidationFieldError[] {
  if (err.code !== 4220) {
    return []
  }
  const data = err.data
  if (data === null || typeof data !== 'object') {
    return []
  }

  const fields = (data as { fields?: unknown }).fields
  if (fields === undefined || fields === null) {
    return []
  }

  const items: ValidationFieldError[] = []

  if (Array.isArray(fields)) {
    for (const item of fields) {
      if (item === null || typeof item !== 'object') {
        continue
      }
      const record = item as { field?: string; message?: string }
      const field = record.field?.trim()
      const fieldMessage = record.message?.trim()
      if (field && fieldMessage) {
        items.push({ field, message: fieldMessage })
      } else if (fieldMessage) {
        items.push({ field: '_form', message: fieldMessage })
      }
    }
  } else if (typeof fields === 'object') {
    for (const [key, value] of Object.entries(fields as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        const text = value.map(String).filter(Boolean).join('；')
        if (text) {
          items.push({ field: key, message: text })
        }
      } else if (value != null && String(value).trim()) {
        items.push({ field: key, message: String(value).trim() })
      }
    }
  }

  return items
}

/** L1 4220 data.fields：dict 或 { field, message }[] */
function formatValidationFields(err: L1Error): string | undefined {
  const items = extractValidationFieldErrors(err)
  if (items.length === 0) {
    return undefined
  }
  return items
    .map((item) =>
      item.field === '_form' ? item.message : `${item.field}: ${item.message}`,
    )
    .join('；')
}

/** 4040 message、4220 fields/message 等业务向详情 */
function resolveBackendDetailText(err: L1Error): string | undefined {
  if (err.code === 4220) {
    const fromFields = formatValidationFields(err)
    if (fromFields) {
      return fromFields
    }
  }

  const backendMessage = err.message?.trim()
  if (BACKEND_DETAIL_CODES.has(err.code) && backendMessage) {
    return backendMessage
  }

  return undefined
}

/**
 * 解析展示文案：errorI18nKey > 4040/4220 后端详情 > 码表 > 5xxx 业务码 > message > generic
 */
export function formatApiErrorMessage(
  err: unknown,
  errorI18nKey?: string,
): string {
  const l1Err = isL1Error(err) ? err : toL1Error(err)
  return resolveErrorText(l1Err, errorI18nKey)
}

/** Ant Design Vue Form：将 4220 fields 写入表单项 errors */
export function applyValidationErrorsToForm(
  form:
    | {
        setFields?: (fields: { name: string | string[]; errors: string[] }[]) => void
      }
    | null
    | undefined,
  err: unknown,
): boolean {
  const l1Err = isL1Error(err) ? err : toL1Error(err)
  const fieldErrors = extractValidationFieldErrors(l1Err)
  if (!form?.setFields || fieldErrors.length === 0) {
    return false
  }
  form.setFields(
    fieldErrors.map((item) => ({
      name: item.field.includes('.') ? item.field.split('.') : item.field,
      errors: [item.message],
    })),
  )
  return true
}

function resolveErrorText(err: L1Error, errorI18nKey?: string): string {
  if (errorI18nKey) {
    const translated = i18n.global.t(errorI18nKey)
    if (translated !== errorI18nKey) {
      return translated
    }
  }

  const backendDetail = resolveBackendDetailText(err)
  if (backendDetail) {
    return backendDetail
  }

  const businessKey = L1_BUSINESS_CODE_I18N[err.code]
  if (businessKey) {
    const translated = i18n.global.t(businessKey)
    if (translated !== businessKey) {
      return translated
    }
  }

  const codeKey = L1_CODE_I18N[err.code]
  if (codeKey) {
    const translated = i18n.global.t(codeKey)
    if (translated !== codeKey) {
      return translated
    }
  }

  if (err.message) {
    return err.message
  }

  return i18n.global.t('error.generic')
}

/** 300ms 内相同 code+message 去重 */
function isDuplicateToast(err: L1Error): boolean {
  const key = `${err.code}:${err.message}`
  const now = Date.now()
  if (key === lastDedupeKey && now - lastDedupeAt < DEDUPE_MS) {
    return true
  }
  lastDedupeKey = key
  lastDedupeAt = now
  return false
}

/**
 * 展示 API 错误 toast（含 300ms 去重）
 */
export function showApiError(
  err: unknown,
  options?: ShowApiErrorOptions
): void {
  const l1Err = toL1Error(err)
  if (isDuplicateToast(l1Err)) {
    return
  }
  message.error(resolveErrorText(l1Err, options?.errorI18nKey))
}

/**
 * 登录/注册页：会话恢复类 4010 不弹 toast；登录 POST 失败仍展示后端文案
 */
export function shouldSkip4010Toast(
  err: L1Error,
  requestMeta?: Pick<MaybePresentErrorConfig, 'requestUrl' | 'requestMethod'>,
): boolean {
  if (err.code !== 4010) {
    return false
  }
  if (!isAuthRoutePath(window.location.pathname)) {
    return false
  }
  const method = (requestMeta?.requestMethod ?? 'get').toLowerCase()
  const url = requestMeta?.requestUrl ?? ''
  if (
    method === 'post' &&
    (url.includes('/auth/login') || url.includes('/auth/register'))
  ) {
    return false
  }
  return true
}

/** 登录页 4010 白名单：不弹全局未授权 toast */
function shouldSkip401Toast(
  err: L1Error,
  config?: MaybePresentErrorConfig,
): boolean {
  return shouldSkip4010Toast(err, config)
}

/** 刷新批处理桶：聚合后展示一条主错误 */
function flushBatch(batchId: string): void {
  const bucket = batchBuckets.get(batchId)
  if (!bucket) {
    return
  }
  batchBuckets.delete(batchId)
  if (bucket.timer !== null) {
    clearTimeout(bucket.timer)
  }

  const primary = pickPrimaryError(bucket.errors)
  if (primary) {
    showApiError(primary, bucket.options)
  }
}

/** 将错误加入批处理桶，延迟聚合展示 */
function enqueueBatchError(
  batchId: string,
  err: L1Error,
  options?: ShowApiErrorOptions
): void {
  let bucket = batchBuckets.get(batchId)
  if (!bucket) {
    bucket = { errors: [], options, timer: null }
    batchBuckets.set(batchId, bucket)
  }

  bucket.errors.push(err)
  if (options?.errorI18nKey && !bucket.options?.errorI18nKey) {
    bucket.options = { ...bucket.options, errorI18nKey: options.errorI18nKey }
  }

  if (bucket.timer !== null) {
    clearTimeout(bucket.timer)
  }
  bucket.timer = setTimeout(() => flushBatch(batchId), BATCH_FLUSH_MS)
}

/**
 * 按请求配置决定是否展示错误（skipErrorToast、401 白名单、errorBatchId）
 */
export function maybePresentError(
  err: unknown,
  config?: MaybePresentErrorConfig
): void {
  if (config?.skipErrorToast) {
    return
  }

  const l1Err = isL1Error(err) ? err : toL1Error(err)
  if (shouldSkip401Toast(l1Err, config)) {
    return
  }

  const options: ShowApiErrorOptions | undefined = config?.errorI18nKey
    ? { errorI18nKey: config.errorI18nKey }
    : undefined

  if (config?.errorBatchId) {
    enqueueBatchError(config.errorBatchId, l1Err, options)
    return
  }

  showApiError(l1Err, options)
}

/** 测试用：重置去重与批处理状态 */
export function resetErrorPresenterState(): void {
  lastDedupeKey = ''
  lastDedupeAt = 0
  for (const bucket of batchBuckets.values()) {
    if (bucket.timer !== null) {
      clearTimeout(bucket.timer)
    }
  }
  batchBuckets.clear()
}
